import React from 'react';
import { Mail, Shield, Key, UserCheck, UserX, RefreshCw, Pencil } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Member, Role } from '../../../services/memberService';
import { useMemberLabels } from './memberLabels';

interface MemberListItemProps {
  member: Member;
  currentUserId?: string;
  canWrite: boolean;
  canDelete: boolean;
  roles: Role[];
  onEditRole: (member: Member) => void;
  onResetPassword: (member: Member) => void;
  onRemove: (member: Member) => void;
  onReactivate: (member: Member) => void;
}

/** A single row in the members list: avatar, name/email, role, status and row actions. */
export const MemberListItem: React.FC<MemberListItemProps> = ({
  member,
  currentUserId,
  canWrite,
  canDelete,
  roles,
  onEditRole,
  onResetPassword,
  onRemove,
  onReactivate,
}) => {
  const { t } = useLanguage();
  const { statusLabel, roleDisplay } = useMemberLabels(roles);
  const isSelf = member.id === currentUserId;
  // Admins and the current user cannot have their role changed or be removed.
  const isProtected = member.role === 'admin' || isSelf;

  return (
    <li data-test-id={`member-row-${member.id}`} className={cn(!member.is_active && 'bg-gray-50 opacity-75')}>
      <div className="px-4 py-4 flex items-center sm:px-6">
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
              {member.full_name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="ml-4 truncate">
              <div className="flex text-sm">
                <p className="font-medium text-primary-600 truncate">{member.full_name || t('settingsMember.values.unknown')}</p>
                <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                  {isSelf && t('settingsMember.values.you')}
                </p>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Mail className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                <p>{member.email}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
            <div className="flex items-center text-sm text-gray-500">
              <Shield className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
              <p data-test-id={`member-role-${member.id}`}>{roleDisplay(member.role)}</p>
            </div>
            <span
              data-test-id={`member-status-${member.id}`}
              className={cn(
                'px-2 inline-flex text-xs leading-5 font-semibold rounded-full items-center gap-1',
                !member.is_active
                  ? 'bg-red-100 text-red-800'
                  : member.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
              )}
            >
              {!member.is_active ? (
                <>
                  <UserX className="h-3 w-3" />
                  {t('settingsMember.status.inactive', undefined, 'Inactive')}
                </>
              ) : (
                <>
                  {member.status === 'active' ? <UserCheck className="h-3 w-3" /> : null}
                  {statusLabel(member.status)}
                </>
              )}
            </span>
          </div>
        </div>
        {(canWrite || canDelete) && (
          <div className="ml-5 flex-shrink-0 flex items-center gap-2">
            {canWrite && !isProtected && member.is_active && (
              <button
                data-test-id={`member-edit-role-button-${member.id}`}
                onClick={() => onEditRole(member)}
                className="text-gray-400 hover:text-primary-600 transition-colors"
                title={t('settingsMember.actions.editRole')}
              >
                <Pencil className="h-5 w-5" />
              </button>
            )}
            {canWrite && (
              <button
                data-test-id={`member-reset-password-button-${member.id}`}
                onClick={() => onResetPassword(member)}
                className="text-gray-400 hover:text-primary-600 transition-colors"
                title={t('settingsMember.actions.resetPassword')}
              >
                <Key className="h-5 w-5" />
              </button>
            )}
            {canDelete && !isProtected && (
              member.is_active ? (
                <button
                  data-test-id={`member-remove-button-${member.id}`}
                  onClick={() => onRemove(member)}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                  title={t('settingsMember.actions.removeMember')}
                >
                  <UserX className="h-5 w-5" />
                </button>
              ) : (
                <button
                  data-test-id={`member-reactivate-button-${member.id}`}
                  onClick={() => onReactivate(member)}
                  className="text-gray-400 hover:text-green-600 transition-colors"
                  title={t('settingsMember.actions.reactivateMember', undefined, 'Reactivate Member')}
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              )
            )}
          </div>
        )}
      </div>
    </li>
  );
};
